/**
 * Play-failure copy map for PlayCardModal.
 *
 * The backend play route (`modules/cards/routes/publicRoutes.ts` →
 * `services/respect/cardService.ts::playCard`) rejects a play in two shapes,
 * both HTTP 400 (RTK Query's `unwrap()` throws a `FetchBaseQueryError`):
 *
 *  1. A `CardPlayError` raised during effect activation, or the treasury's
 *     `insufficient_respect` refusal on a priced play → the result carries an
 *     `errorCode` string (`services/respect/errors.ts`,
 *     `services/respect/treasuryService.ts`). `CardPlayError.code` is a
 *     free-form string with no enum, so any code missing from the map below
 *     must degrade gracefully to the server message.
 *  2. The code-LESS "Card not found or already played." 400 — no `errorCode`.
 *     The realistic trigger is a stale tile: play a copy on the phone, then tap
 *     Play on a still-mounted /cards tab whose `playableInstanceId` has since
 *     moved on. We give that its own friendly case rather than echoing the raw
 *     "not found" string, which reads like a bug to the player.
 */

export interface PlayErrorCopy {
  title: string;
  hint: string;
}

/**
 * The `CardPlayError` codes the play route emits today
 * (`grep new CardPlayError`), plus the extra `blocked`-playability codes the
 * pre-flight surfaces (`evaluatePlayability` → `unknown_card`, the read-only
 * Heist guard codes, Long Shot's `recent_aotd_author`, and the five Rematch
 * standings from `resolveRematchStanding`). The pre-flight only carries a
 * `code`, not a message, so these need their own copy here rather than echoing
 * a raw token.
 */
const KNOWN_CODE_COPY: Record<string, PlayErrorCopy> = {
  too_late: {
    title: "Too late for today",
    hint: "This card had to be played before today's AOTD scheduled time. Try again tomorrow.",
  },
  double_stacked: {
    title: "Already running",
    hint: "An effect of this kind is already active. Wait for it to finish before playing another.",
  },
  missing_target: {
    title: "Pick a target first",
    hint: "This card needs a player to aim at. Choose one, then play it again.",
  },
  self_target_not_allowed: {
    title: "Can't target yourself",
    hint: "Pick another player as the target for this card.",
  },
  ineligible_target: {
    title: "That target isn't eligible",
    hint: "The player you picked can't be targeted right now. Choose a different one.",
  },
  too_many_targets: {
    title: "Too many names",
    hint: "This card takes at most three players. Drop one and try again.",
  },
  mute_cooldown: {
    title: "Already muted yesterday",
    hint: "Nobody can be muted two days running. Pick someone else.",
  },
  // Treasury charge on a priced play (`playCost`) — the card never left the
  // hand, so this is a "come back richer", not a failure the player can retry
  // their way out of.
  insufficient_respect: {
    title: "Not enough respect",
    hint: "This card costs respect you don't have yet. Race, win, or wait for the treasury to pay out, then play it.",
  },
  // blocked-playability-only codes (evaluatePlayability + canAttemptHeist)
  unknown_card: {
    title: "Card unavailable",
    hint: "We can't recognise this card right now. Refresh your hand and try again.",
  },
  aotd_unclaimed: {
    title: "Nothing to steal yet",
    hint: "Today's AOTD hasn't been claimed — there's no claim for a Heist to take.",
  },
  self_steal_rejected: {
    title: "That's already yours",
    hint: "You can't Heist your own AOTD claim.",
  },
  before_window: {
    title: "Too early to strike",
    hint: "The Heist opens when today's art appears. Wait for it.",
  },
  after_window: {
    title: "The window has closed",
    hint: "A Heist only works in the first 4.5 minutes after today's art appears.",
  },
  rate_limited: {
    title: "Already attempted today",
    hint: "You've used your Heist for today. Try again tomorrow.",
  },
  no_staged_emojis: {
    title: "Nothing staged",
    hint: "Stage the emojis you'd take before attempting a Heist.",
  },
  // Long Shot's own gate: the card is for the players who are not winning.
  recent_aotd_author: {
    title: "You've been winning",
    hint: "Long Shot is for the players who haven't taken an AOTD lately. Sit out a week of wins and it opens up again.",
  },
  // Rematch's gate (`resolveRematchStanding`) is the whole card, so all five of
  // its refusals land here.
  no_race_today: {
    title: "No race to rematch",
    hint: "Today's race hasn't produced a board yet. Wait for people to finish, then call it.",
  },
  race_undecided: {
    title: "The race isn't over",
    hint: "Today's winner hasn't been settled yet. Once the result lands you can demand a rematch.",
  },
  did_not_race: {
    title: "You weren't in it",
    hint: "You didn't race today, so there's nothing to rematch. Show up tomorrow.",
  },
  won_today: {
    title: "You took today's art",
    hint: "Rematch is for the near miss, not the win. Hold on to it for a day you lose narrowly.",
  },
  not_close_enough: {
    title: "Not close enough",
    hint: "Rematch only plays after a loss by a hair. Today's gap was too wide.",
  },
  // The AOTD card hand (modules/aotd/quiz/CONTRACT.md): the play window and
  // the gates on Second Guess and Reclamation. They arrive both as a blocked
  // playability code and as the play route's errorCode.
  outside_play_window: {
    title: "Not now",
    hint: "You can play cards from the unlock until 45 minutes after it.",
  },
  quiz_not_open: {
    title: "No guessing open",
    hint: "Second Guess only works while today's AOTD guessing is open.",
  },
  not_quiz_player: {
    title: "Not in today's guessing",
    hint: "Only members play AOTD guessing, so there's no guess to repeat.",
  },
  second_guess_used: {
    title: "Already have two",
    hint: "You already have a second guess in today's guessing.",
  },
  no_used_emojis: {
    title: "Nothing to get back",
    hint: "You haven't used any emojis on an AOTD yet, so Reclamation has nothing to return.",
  },
  selection_required: {
    title: "Pick your emojis",
    hint: "Reclamation needs the emojis you want back. Play it from your hand on the AOTD page and pick them there.",
  },
  selection_too_many: {
    title: "Too many emojis",
    hint: "Reclamation gives back up to 10 emojis. Drop a few and try again.",
  },
  emoji_not_used: {
    title: "Not one of yours",
    hint: "One of those emojis isn't on your used list any more. Refresh and pick again.",
  },
  hand_over_limit: {
    title: "Too many cards",
    hint: "Your deck holds 4 now. Pick the 4 you keep on the AOTD page, then play.",
  },
  play_in_progress: {
    title: "Already playing",
    hint: "That card is being played right now. Checking your hand again.",
  },
};

/**
 * Known codes after which the hand on screen may be out of date: the caller
 * refetches it (`staleInstance`). `play_in_progress` is a double tap or a
 * retry racing the first play; the refetch shows whether the card is still
 * in hand.
 */
const REFRESH_CODES = new Set(["play_in_progress"]);

/** The copy for a known play or blocked code, or null for an unknown one. */
export function knownPlayErrorCopy(code: string | undefined): PlayErrorCopy | null {
  return code && KNOWN_CODE_COPY[code] ? KNOWN_CODE_COPY[code] : null;
}

/** Copy for the code-less "already played" stale-tile 400. */
const STALE_INSTANCE_COPY: PlayErrorCopy = {
  title: "Card already used",
  hint: "This copy has already left your hand — you may have played it on another tab or device. Refreshing your hand.",
};

/** Substrings that mark the code-less "already played / not found" 400. */
const STALE_MESSAGE_MARKERS = ["already played", "not found"];

/**
 * Pulls `{ status, data: { message?, errorCode? } }` out of the RTK Query
 * error thrown by `usePlayCardMutation().unwrap()` (or a caught thrown value).
 */
function readPlayError(error: unknown): { message?: string; errorCode?: string } {
  if (
    error &&
    typeof error === "object" &&
    "data" in error &&
    error.data &&
    typeof error.data === "object"
  ) {
    const data = error.data as { message?: unknown; errorCode?: unknown };
    return {
      message: typeof data.message === "string" ? data.message : undefined,
      errorCode:
        typeof data.errorCode === "string" ? data.errorCode : undefined,
    };
  }
  return {};
}

/**
 * Maps a play failure to display copy.
 *
 * Resolution order:
 *  - a known `errorCode` → its mapped `{ title, hint }`;
 *  - the code-less "already played"/"not found" 400 → the stale-tile case;
 *  - otherwise the server `message` rendered verbatim (unknown free-string
 *    code included), falling back to a generic line when even that is absent.
 *
 * `staleInstance` is surfaced so the caller can also invalidate its hand view
 * (the tile it played from is gone, or another play of it is running).
 */
export function resolvePlayError(error: unknown): PlayErrorCopy & {
  staleInstance: boolean;
} {
  const { message, errorCode } = readPlayError(error);

  if (errorCode && KNOWN_CODE_COPY[errorCode]) {
    return { ...KNOWN_CODE_COPY[errorCode], staleInstance: REFRESH_CODES.has(errorCode) };
  }

  const looksStale =
    !errorCode &&
    typeof message === "string" &&
    STALE_MESSAGE_MARKERS.some((marker) =>
      message.toLowerCase().includes(marker),
    );

  if (looksStale) {
    return { ...STALE_INSTANCE_COPY, staleInstance: true };
  }

  return {
    title: "Couldn't play this card",
    hint: message || "Something went wrong. Try again in a moment.",
    staleInstance: false,
  };
}

/**
 * Copy for the hand's own refusals: `POST /cards/offers/:id/resolve` and the
 * keep-4 picker's `POST /cards/keep`. Both answer `{ success: false, code,
 * message }`.
 */
const HAND_ERROR_COPY: Record<string, string> = {
  offer_not_found: "That card isn't waiting for you any more.",
  offer_not_pending: "That card was already sorted out.",
  discard_required: "Your hand is full. Pick the card you throw away.",
  discard_not_in_hand: "That card already left your hand. Pick another one.",
  hand_over_limit: "Pick the 4 cards you keep first.",
  not_over_limit: "Your hand already fits.",
  bad_selection: "Pick exactly 4 cards.",
  not_in_hand: "One of those already left your hand. Pick again.",
};

export function handErrorText(error: unknown): string {
  if (error && typeof error === "object" && "data" in error && error.data && typeof error.data === "object") {
    const data = error.data as { code?: unknown; message?: unknown };
    if (typeof data.code === "string" && HAND_ERROR_COPY[data.code]) return HAND_ERROR_COPY[data.code];
    if (typeof data.message === "string" && data.message) return data.message;
  }
  return "Something went wrong. Try again in a moment.";
}
